import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, X, Fuel, Loader2, Search, Upload, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface OwnerFuelRow {
  id: string;
  entry_date: string;
  truck_id: string | null;
  truck_number: string;
  state: string;
  gallons: number;
  price_per_gallon: number | null;
  total_price: number | null;
  notes: string | null;
  created_at: string;
}

interface TruckOption {
  id: string;
  unit_number: string;
}

interface OwnerFuelViewProps {
  year: number;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV',
  'WI','WY','DC',
];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];
const JURISDICTIONS = [...US_STATES, ...CA_PROVINCES];

interface FormState {
  entry_date: string;
  truck_id: string;
  truck_number: string;
  state: string;
  gallons: string;
  price_per_gallon: string;
  total_price: string;
  notes: string;
}

function emptyForm(): FormState {
  return {
    entry_date: new Date().toISOString().slice(0, 10),
    truck_id: '',
    truck_number: '',
    state: '',
    gallons: '',
    price_per_gallon: '',
    total_price: '',
    notes: '',
  };
}

// ---------- CSV import ----------
// One row per receipt. Column order does not matter; headers are matched by
// name (case-insensitive) with a few aliases so a hand-made sheet still works.
const CSV_TEMPLATE_HEADERS = ['date', 'truck', 'state', 'gallons', 'price_per_gallon', 'total_price', 'notes'];
const CSV_TEMPLATE_ROWS = [
  ['2026-10-02', '312', 'GA', '120.500', '3.899', '469.83', 'Pilot - Savannah'],
  ['10/03/2026', '312', 'SC', '98.2', '', '385.14', 'Receipt #4471'],
  ['2026-10-05', '105', 'TN', '134', '3.75', '', ''],
];
const HEADER_ALIASES: Record<string, keyof CsvParsedRow> = {
  date: 'date', entry_date: 'date', 'fuel date': 'date', 'purchase date': 'date',
  truck: 'truck', 'truck #': 'truck', 'truck number': 'truck', truck_number: 'truck', unit: 'truck', 'unit #': 'truck', 'unit number': 'truck', unit_number: 'truck',
  state: 'state', jurisdiction: 'state', province: 'state', 'state/province': 'state',
  gallons: 'gallons', gal: 'gallons', gals: 'gallons', quantity: 'gallons', qty: 'gallons',
  price_per_gallon: 'price', 'price per gallon': 'price', price: 'price', ppg: 'price', '$/gal': 'price', 'price/gal': 'price',
  total_price: 'total', total: 'total', amount: 'total', 'total paid': 'total', cost: 'total',
  notes: 'notes', note: 'notes', memo: 'notes', description: 'notes',
};
interface CsvParsedRow { date: string; truck: string; state: string; gallons: string; price: string; total: string; notes: string }
interface CsvPreviewRow {
  line: number;
  raw: CsvParsedRow;
  errors: string[];
  payload: {
    entry_date: string; truck_id: string | null; truck_number: string; state: string;
    gallons: number; price_per_gallon: number | null; total_price: number | null; notes: string | null;
  } | null;
}

// Minimal RFC-4180-ish parser: handles quoted fields, embedded commas/quotes/newlines, CRLF.
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(f => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some(f => f.trim() !== '')) rows.push(row);
  return rows;
}

// Accepts YYYY-MM-DD, M/D/YYYY, M-D-YYYY, M/D/YY. Returns YYYY-MM-DD or null.
function normalizeCsvDate(v: string): string | null {
  const t = v.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(t);
  if (m) {
    const yy = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${yy}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  return null;
}

function parseMoney(v: string): number | null {
  const t = v.replace(/[$,\s]/g, '').trim();
  if (!t) return null;
  const n = parseFloat(t);
  return isNaN(n) ? null : n;
}

function downloadOwnerFuelTemplate() {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = [CSV_TEMPLATE_HEADERS, ...CSV_TEMPLATE_ROWS].map(r => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'owner-fuel-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function OwnerFuelView({ year }: OwnerFuelViewProps) {
  const toast = useToast();
  const [rows, setRows] = useState<OwnerFuelRow[]>([]);
  const [trucks, setTrucks] = useState<TruckOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<OwnerFuelRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<OwnerFuelRow | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewRow[] | null>(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { load(); }, [year]);

  async function load() {
    try {
      setLoading(true);
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;
      const [fuelRes, trucksRes] = await Promise.all([
        supabase
          .from('owner_fuel_entries')
          .select('*')
          .gte('entry_date', start)
          .lte('entry_date', end)
          .order('entry_date', { ascending: false }),
        supabase.from('trucks').select('id, unit_number').order('unit_number'),
      ]);
      if (fuelRes.error) throw fuelRes.error;
      if (trucksRes.error) throw trucksRes.error;
      setRows((fuelRes.data || []) as OwnerFuelRow[]);
      setTrucks((trucksRes.data || []) as TruckOption[]);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load owner fuel entries');
    } finally {
      setLoading(false);
    }
  }

  const totals = useMemo(() => {
    const filtered = rows.filter(r => matchesSearch(r, search));
    return {
      count: filtered.length,
      gallons: filtered.reduce((s, r) => s + Number(r.gallons || 0), 0),
      cost: filtered.reduce((s, r) => s + Number(r.total_price || 0), 0),
      filtered,
    };
  }, [rows, search]);

  function openAdd() {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  }

  function openEdit(row: OwnerFuelRow) {
    setEditing(row);
    setForm({
      entry_date: row.entry_date,
      truck_id: row.truck_id || '',
      truck_number: row.truck_number,
      state: row.state,
      gallons: String(row.gallons ?? ''),
      price_per_gallon: row.price_per_gallon != null ? String(row.price_per_gallon) : '',
      total_price: row.total_price != null ? String(row.total_price) : '',
      notes: row.notes || '',
    });
    setShowModal(true);
  }

  // When user types gallons or price, auto-fill total_price unless they've
  // already overridden it. Same in reverse for price_per_gallon. Kept lightweight.
  function onGallonsChange(v: string) {
    setForm(f => {
      const next = { ...f, gallons: v };
      const g = parseFloat(v);
      const p = parseFloat(f.price_per_gallon);
      if (!isNaN(g) && !isNaN(p) && (f.total_price === '' || f.total_price === autoTotal(f))) {
        next.total_price = (g * p).toFixed(2);
      }
      return next;
    });
  }

  function onPriceChange(v: string) {
    setForm(f => {
      const next = { ...f, price_per_gallon: v };
      const g = parseFloat(f.gallons);
      const p = parseFloat(v);
      if (!isNaN(g) && !isNaN(p) && (f.total_price === '' || f.total_price === autoTotal(f))) {
        next.total_price = (g * p).toFixed(2);
      }
      return next;
    });
  }

  function autoTotal(f: FormState) {
    const g = parseFloat(f.gallons);
    const p = parseFloat(f.price_per_gallon);
    if (isNaN(g) || isNaN(p)) return '';
    return (g * p).toFixed(2);
  }

  // As the user types a truck number, auto-link to the fleet when the typed
  // value matches an existing unit_number; otherwise leave truck_id null so
  // the entry is stored "unlinked".
  function onTruckNumberChange(v: string) {
    const trimmed = v.trim();
    const hit = trimmed
      ? trucks.find(t => t.unit_number.toLowerCase() === trimmed.toLowerCase())
      : null;
    setForm(f => ({
      ...f,
      truck_number: v,
      truck_id: hit ? hit.id : '',
    }));
  }

  async function save() {
    const gallons = parseFloat(form.gallons);
    const state = form.state.trim().toUpperCase();
    if (!form.entry_date) return toast.error('Date is required');
    if (!form.truck_number.trim()) return toast.error('Truck # is required');
    if (!state || state.length !== 2) return toast.error('Enter a 2-letter state/province code');
    if (isNaN(gallons) || gallons <= 0) return toast.error('Gallons must be greater than 0');

    const payload = {
      entry_date: form.entry_date,
      truck_id: form.truck_id || null,
      truck_number: form.truck_number.trim(),
      state,
      gallons,
      price_per_gallon: form.price_per_gallon ? parseFloat(form.price_per_gallon) : null,
      total_price: form.total_price ? parseFloat(form.total_price) : null,
      notes: form.notes.trim() || null,
    };

    try {
      setSaving(true);
      if (editing) {
        const { error } = await supabase
          .from('owner_fuel_entries')
          .update(payload)
          .eq('id', editing.id);
        if (error) throw error;
        toast.success('Owner fuel entry updated');
      } else {
        const { data: userData } = await supabase.auth.getUser();
        const { error } = await supabase
          .from('owner_fuel_entries')
          .insert({ ...payload, created_by: userData.user?.id || null });
        if (error) throw error;
        toast.success('Owner fuel entry added');
      }
      setShowModal(false);
      setEditing(null);
      load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  }

  // Parse a chosen CSV into a validated preview. Nothing is written here.
  async function onCsvChosen(file: File) {
    setCsvFileName(file.name);
    try {
      const text = await file.text();
      const table = parseCsvText(text);
      if (table.length < 2) { toast.error('CSV has no data rows (header + at least one line expected)'); return; }
      const header = table[0].map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
      const colIndex: Partial<Record<keyof CsvParsedRow, number>> = {};
      header.forEach((h, i) => {
        const key = HEADER_ALIASES[h];
        if (key && colIndex[key] === undefined) colIndex[key] = i;
      });
      const required: (keyof CsvParsedRow)[] = ['date', 'truck', 'state', 'gallons'];
      const missingCols = required.filter(k => colIndex[k] === undefined);
      if (missingCols.length) {
        toast.error(`CSV is missing required column(s): ${missingCols.join(', ')}. Download the template to see the expected headers.`);
        return;
      }
      const get = (r: string[], k: keyof CsvParsedRow) => (colIndex[k] === undefined ? '' : (r[colIndex[k] as number] ?? '')).trim();

      const preview: CsvPreviewRow[] = table.slice(1).map((r, idx) => {
        const raw: CsvParsedRow = {
          date: get(r, 'date'), truck: get(r, 'truck'), state: get(r, 'state'),
          gallons: get(r, 'gallons'), price: get(r, 'price'), total: get(r, 'total'), notes: get(r, 'notes'),
        };
        const errors: string[] = [];
        const entry_date = normalizeCsvDate(raw.date);
        if (!entry_date) errors.push('Date must be YYYY-MM-DD or M/D/YYYY');
        const truck_number = raw.truck.replace(/^#/, '').trim();
        if (!truck_number) errors.push('Truck # is required');
        const state = raw.state.toUpperCase();
        if (!JURISDICTIONS.includes(state)) errors.push('State must be a 2-letter US state or CA province code');
        const gallons = parseMoney(raw.gallons);
        if (gallons === null || gallons <= 0) errors.push('Gallons must be a number greater than 0');
        const price = parseMoney(raw.price);
        if (raw.price && price === null) errors.push('Price per gallon is not a number');
        let total = parseMoney(raw.total);
        if (raw.total && total === null) errors.push('Total price is not a number');
        // Same convenience as the form: fill total from gallons × price when absent.
        if (total === null && gallons !== null && price !== null) total = Math.round(gallons * price * 100) / 100;

        const hit = truck_number
          ? trucks.find(t => t.unit_number.toLowerCase() === truck_number.toLowerCase())
          : null;

        return {
          line: idx + 2,
          raw,
          errors,
          payload: errors.length ? null : {
            entry_date: entry_date as string,
            truck_id: hit ? hit.id : null,
            truck_number,
            state,
            gallons: gallons as number,
            price_per_gallon: price,
            total_price: total,
            notes: raw.notes || null,
          },
        };
      });
      setCsvPreview(preview);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read that CSV');
    }
  }

  async function importCsv() {
    if (!csvPreview) return;
    const good = csvPreview.filter(r => r.payload).map(r => r.payload!);
    if (good.length === 0) { toast.error('No valid rows to import'); return; }
    try {
      setCsvImporting(true);
      const { data: userData } = await supabase.auth.getUser();
      const createdBy = userData.user?.id || null;
      let inserted = 0;
      for (let i = 0; i < good.length; i += 200) {
        const chunk = good.slice(i, i + 200).map(p => ({ ...p, created_by: createdBy }));
        const { error } = await supabase.from('owner_fuel_entries').insert(chunk);
        if (error) throw error;
        inserted += chunk.length;
      }
      toast.success(`Imported ${inserted} owner fuel entr${inserted === 1 ? 'y' : 'ies'}`);
      setCsvPreview(null);
      setCsvFileName('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase
        .from('owner_fuel_entries')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success('Owner fuel entry deleted');
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete entry');
    }
  }

  return (
    <div className="space-y-4">
      {csvPreview && (() => {
        const valid = csvPreview.filter(r => r.payload);
        const invalid = csvPreview.filter(r => !r.payload);
        const unlinked = valid.filter(r => !r.payload!.truck_id).length;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Import owner fuel from CSV</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{csvFileName} · {csvPreview.length} row{csvPreview.length === 1 ? '' : 's'} read</p>
                </div>
                <button onClick={() => { setCsvPreview(null); setCsvFileName(''); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {valid.length} ready to import</span>
                {invalid.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-rose-700"><AlertTriangle className="w-4 h-4" /> {invalid.length} with errors (skipped)</span>
                )}
                {unlinked > 0 && (
                  <span className="text-slate-600">{unlinked} truck #{unlinked === 1 ? '' : 's'} not in the fleet list — saved as unlinked</span>
                )}
              </div>
              <div className="overflow-auto flex-1">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2">Line</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Truck</th>
                      <th className="px-3 py-2">State</th>
                      <th className="px-3 py-2 text-right">Gallons</th>
                      <th className="px-3 py-2 text-right">$ / gal</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">Notes</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {csvPreview.map(r => (
                      <tr key={r.line} className={r.payload ? '' : 'bg-rose-50'}>
                        <td className="px-3 py-1.5 text-slate-400">{r.line}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{r.payload ? r.payload.entry_date : r.raw.date}</td>
                        <td className="px-3 py-1.5 font-medium">
                          {r.payload ? r.payload.truck_number : r.raw.truck}
                          {r.payload && !r.payload.truck_id && <span className="ml-1 text-xs text-slate-400">(unlinked)</span>}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.payload ? r.payload.state : r.raw.state}</td>
                        <td className="px-3 py-1.5 text-right">{r.payload ? r.payload.gallons.toLocaleString(undefined, { maximumFractionDigits: 3 }) : r.raw.gallons}</td>
                        <td className="px-3 py-1.5 text-right text-slate-600">{r.payload ? (r.payload.price_per_gallon != null ? `$${r.payload.price_per_gallon.toFixed(3)}` : '—') : r.raw.price}</td>
                        <td className="px-3 py-1.5 text-right">{r.payload ? (r.payload.total_price != null ? `$${r.payload.total_price.toFixed(2)}` : '—') : r.raw.total}</td>
                        <td className="px-3 py-1.5 max-w-[160px] truncate text-slate-600" title={r.raw.notes}>{r.raw.notes || '—'}</td>
                        <td className="px-3 py-1.5 text-xs">
                          {r.payload
                            ? <span className="text-emerald-700">OK</span>
                            : <span className="text-rose-700">{r.errors.join('; ')}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  Required columns: <span className="font-mono">date, truck, state, gallons</span>. Optional: <span className="font-mono">price_per_gallon, total_price, notes</span>. Rows with errors are skipped; fix them in the file and import again.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setCsvPreview(null); setCsvFileName(''); }}
                    disabled={csvImporting}
                    className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={importCsv}
                    disabled={csvImporting || valid.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    {csvImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {csvImporting ? 'Importing…' : `Import ${valid.length} entr${valid.length === 1 ? 'y' : 'ies'}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Fuel className="w-5 h-5 text-amber-600" />
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Owner Fuel ({year})</h2>
              <p className="text-xs text-slate-500">
                Fuel paid by owner-operators. These gallons are added to the Return tab totals.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadOwnerFuelTemplate}
              title="Download a CSV template showing the columns to fill in"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium"
            >
              <Download className="w-4 h-4" /> CSV Template
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) onCsvChosen(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => csvInputRef.current?.click()}
              title="Import many receipts at once from a CSV file"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50 text-sm font-medium"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Entry
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 border-b border-slate-100 bg-slate-50">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by truck, state, or notes…"
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="text-xs text-slate-600 flex gap-4">
            <span>Entries: <span className="font-semibold text-slate-900">{totals.count}</span></span>
            <span>Gallons: <span className="font-semibold text-slate-900">{totals.gallons.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></span>
            <span>Total paid: <span className="font-semibold text-slate-900">${totals.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : totals.filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            No owner fuel entries for {year}. Click <span className="font-medium">Add Entry</span> when a receipt comes in.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Truck</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2 text-right">Gallons</th>
                  <th className="px-3 py-2 text-right">$ / gal</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">Notes</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {totals.filtered.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(row.entry_date + 'T00:00:00').toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">
                      {row.truck_number}
                      {!row.truck_id && <span className="ml-1 text-xs text-slate-400">(unlinked)</span>}
                    </td>
                    <td className="px-3 py-2"><span className="inline-flex px-1.5 py-0.5 text-xs rounded bg-slate-100 text-slate-700 font-mono">{row.state}</span></td>
                    <td className="px-3 py-2 text-right">{Number(row.gallons).toLocaleString(undefined, { maximumFractionDigits: 3 })}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{row.price_per_gallon != null ? `$${Number(row.price_per_gallon).toFixed(3)}` : '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{row.total_price != null ? `$${Number(row.total_price).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate text-slate-600" title={row.notes || ''}>{row.notes || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(row)} title="Edit" className="p-1.5 rounded hover:bg-slate-200 text-slate-600">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(row)} title="Delete" className="p-1.5 rounded hover:bg-red-100 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-800">{editing ? 'Edit owner fuel entry' : 'Add owner fuel entry'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Date *</span>
                  <input
                    type="date"
                    value={form.entry_date}
                    onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">State / Province *</span>
                  <input
                    type="text"
                    list="owner-fuel-states"
                    value={form.state}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                    placeholder="e.g. IL"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm uppercase"
                  />
                  <datalist id="owner-fuel-states">
                    {JURISDICTIONS.map(j => <option key={j} value={j} />)}
                  </datalist>
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-medium text-slate-600">Truck # *</span>
                <input
                  type="text"
                  list="owner-fuel-trucks"
                  value={form.truck_number}
                  onChange={e => onTruckNumberChange(e.target.value)}
                  placeholder="Search or type a truck number"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                />
                <datalist id="owner-fuel-trucks">
                  {trucks.map(t => <option key={t.id} value={t.unit_number} />)}
                </datalist>
                <p className="mt-1 text-xs">
                  {form.truck_number.trim()
                    ? form.truck_id
                      ? <span className="text-emerald-600">Linked to fleet truck {form.truck_number}</span>
                      : <span className="text-amber-600">Unlinked — this truck isn't in the fleet, entry will still save</span>
                    : <span className="text-slate-400">Start typing to link to a fleet truck, or enter any number for an unlinked entry.</span>}
                </p>
              </label>

              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Gallons *</span>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.gallons}
                    onChange={e => onGallonsChange(e.target.value)}
                    placeholder="0.000"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-right"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">$ / gallon</span>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.price_per_gallon}
                    onChange={e => onPriceChange(e.target.value)}
                    placeholder="0.000"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-right"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Total $</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.total_price}
                    onChange={e => setForm(f => ({ ...f, total_price: e.target.value }))}
                    placeholder="0.00"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-right"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-medium text-slate-600">Notes</span>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Station, receipt #, anything helpful"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fuel className="w-4 h-4" />}
                {editing ? 'Save changes' : 'Add entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm">
            <div className="p-5 space-y-2">
              <h3 className="text-base font-semibold text-slate-800">Delete owner fuel entry?</h3>
              <p className="text-sm text-slate-600">
                {new Date(deleteTarget.entry_date + 'T00:00:00').toLocaleDateString()} · Truck {deleteTarget.truck_number} · {deleteTarget.state} · {Number(deleteTarget.gallons).toLocaleString()} gal
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function matchesSearch(row: OwnerFuelRow, q: string): boolean {
  if (!q) return true;
  const s = q.toLowerCase();
  return (
    (row.truck_number || '').toLowerCase().includes(s) ||
    (row.state || '').toLowerCase().includes(s) ||
    (row.notes || '').toLowerCase().includes(s)
  );
}
